import React, { useMemo, useRef } from 'react';

type TimeOfDay = 'dawn' | 'day' | 'golden' | 'dusk' | 'night';

function getTimeOfDay(hour: number): TimeOfDay {
  if (hour >= 5 && hour < 9) return 'dawn';
  if (hour >= 9 && hour < 16) return 'day';
  if (hour >= 16 && hour < 19) return 'golden';
  if (hour >= 19 && hour < 21) return 'dusk';
  return 'night';
}

const LOGIN_VIDEOS = [
  '/videos/login/01.mp4',
  '/videos/login/02.mp4',
  '/videos/login/03.mp4',
  '/videos/login/04.mp4',
  '/videos/login/05.mp4',
  '/videos/login/06.mp4',
  '/videos/login/07.mp4',
  '/videos/login/08.mp4',
] as const;

function pickRandomVideo(): string {
  return LOGIN_VIDEOS[Math.floor(Math.random() * LOGIN_VIDEOS.length)]!;
}

interface AuthShellProps {
  children: React.ReactNode;
}

/**
 * Shared auth-scene chrome used by both login form and post-login loading:
 * full-bleed time-of-day video, overlay, grain, shine, Joshua logo, copyright.
 * Parallax is applied via CSS custom props (`--parallax-x`, `--parallax-y`) set
 * imperatively on the root — zero React re-renders per mousemove.
 */
export default function AuthShell({ children }: AuthShellProps) {
  const pageRef = useRef<HTMLDivElement>(null);

  const { tod, videoSrc } = useMemo(() => {
    const h = new Date().getHours();
    return { tod: getTimeOfDay(h), videoSrc: pickRandomVideo() };
  }, []);

  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (prefersReducedMotion || !pageRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
    pageRef.current.style.setProperty('--parallax-x', String(x));
    pageRef.current.style.setProperty('--parallax-y', String(y));
  };

  const handleMouseLeave = () => {
    if (!pageRef.current) return;
    pageRef.current.style.setProperty('--parallax-x', '0');
    pageRef.current.style.setProperty('--parallax-y', '0');
  };

  return (
    <div
      ref={pageRef}
      className="auth-page"
      data-tod={tod}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div className="auth-video-wrapper" aria-hidden="true">
        <video
          key={videoSrc}
          className="auth-video"
          src={videoSrc}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
        />
      </div>

      <div className="auth-overlay" aria-hidden="true" />
      <div className="auth-grain" aria-hidden="true" />
      <div className="auth-shine" aria-hidden="true" />

      <div className="auth-logo-joshua">
        <img src="/logo_joshua.png" alt="Joshua" />
      </div>

      {children}

      <div className="auth-copyright">© {new Date().getFullYear()} Joshua</div>
    </div>
  );
}
